import { 
    useTheme, 
    Box, 
    Typography, 
    Paper, 
    IconButton, 
    Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { formatNumber, formatTZDate } from 'state/helpers';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexCenter from 'components/FlexCenter';


const AdjustmentCard = ({ row, index, canEdit, canDelete, onEdit, onDelete }) => {
    const theme = useTheme();
    const crewMembers = Array.isArray(row.crewMembers)
        ? [...row.crewMembers].sort((a, b) => String(a.crewCode).localeCompare(String(b.crewCode)))
        : [];

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                borderLeft: `2px solid ${theme.palette.primary.main}`, 
                borderRadius: 1 
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    #{index + 1}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold" flex={1} noWrap>
                    {row.description || '-'}
                </Typography>
                {canEdit && (
                    <Tooltip title="Edit">
                        <IconButton 
                            size="small" 
                            onClick={() => onEdit(row._id)}
                            sx={{ color: "primary.dark", '&:hover': { color: "green.deep" } }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canDelete && (
                    <Tooltip title="Delete">
                        <IconButton 
                            size="small" 
                            onClick={() => onDelete(row._id)}
                            sx={{ color: "primary.dark", '&:hover': { color: "green.deep" } }}>
                            <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                {row.adjustmentType || '-'} · {formatTZDate(row.effectiveDate)}
            </Typography>

            <Box display="flex" gap={2} mt={0.75} flexWrap="wrap">
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Amount
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                        {formatNumber(row.amount, 0, 0)}
                    </Typography>
                </Box>
                {row.currency && (
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Currency
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                            {row.currency}
                        </Typography>
                    </Box>
                )}
                {row.isNonTaxableIncome != null && (
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Non-Taxable
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                            {typeof row.isNonTaxableIncome === 'boolean'
                                ? (row.isNonTaxableIncome ? 'Yes' : 'No')
                                : String(row.isNonTaxableIncome)}
                        </Typography>
                    </Box>
                )}
            </Box>

            {crewMembers.length > 0 && (
                <Box mt={0.75}>
                    <Typography variant="caption" color="text.secondary">
                        Crew: <strong>{crewMembers.length}</strong> members
                    </Typography>
                    <Box mt={0.25}>
                        {crewMembers.map((member, i) => (
                            <Box key={i} display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="caption" sx={{ minWidth: 32, fontWeight: 'bold' }}>
                                    {member.crewCode}
                                </Typography>
                                <Typography variant="caption" flex={1}>{member.crewName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    ({member.rank} | {member.base})
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Paper>
    );
};


const IncomeAdjustmentsList = ({ rows, canEdit, canDelete, onEdit, onDelete }) => {
    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            width="100%"
            gap={2}
        >
            {rows.map((row, index) => (
                <AdjustmentCard
                    key={row._id}
                    row={row}
                    index={index}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onEdit={onEdit}
                    onDelete={onDelete}
                />
            ))}
        </Box>
    );
};

export default IncomeAdjustmentsList;
